import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { ButtonDirective, DialogRef, FormFieldImports, LabelDirective, SelectImports } from 'shared/ui-common';

import { DIALOG_DATA, GuidelineImport, Organisation } from '@treetop/shared/model';

import { GuidelineImportUpdateSettingsComponent, ShareUpdateMode } from '../guideline-import-update-settings.component';

export type ImportGuidelineData = {
  automaticUpdates: boolean;
  forwardEmailNotifications: boolean;
  originalGuidelineUniqueId: string;
};

type ImportGuidelineDialogData = {
  uniqueId: string;
  targetOrganisations: Organisation[];
  selectedTargetOrganisationId?: string;
};

@Component({
  selector: 'tt-import-guideline-dialog',
  standalone: true,
  templateUrl: 'import-guideline-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['import-guideline-dialog.component.scss'],
  imports: [
    TranslocoPipe,
    FormsModule,
    GuidelineImportUpdateSettingsComponent,
    ButtonDirective,
    FormFieldImports,
    LabelDirective,
    SelectImports,
  ],
})
export class ImportGuidelineDialogComponent {
  protected dialogRef = inject(DialogRef);
  private data = inject(DIALOG_DATA) as ImportGuidelineDialogData;

  protected updateMode = signal<ShareUpdateMode>(undefined);
  protected forwardEmailNotifications = signal(false);
  protected selectedTargetOrganisationId = signal(this.data.selectedTargetOrganisationId);
  protected targetOrganisations = this.data.targetOrganisations;

  protected importGuidelineData = computed<GuidelineImport>(() => {
    return {
      automaticUpdates: this.updateMode() && this.updateMode() === 'automatic',
      forwardEmailNotifications: this.forwardEmailNotifications(),
      originalGuidelineUniqueId: this.data.uniqueId,
      targetOrganisationId: this.selectedTargetOrganisationId(),
    };
  });

  protected isImportGuidelineDataValid = computed(() => {
    return (
      this.importGuidelineData().automaticUpdates !== undefined &&
      !!this.importGuidelineData().originalGuidelineUniqueId &&
      !!this.importGuidelineData().targetOrganisationId
    );
  });

  protected handleImportClicked() {
    this.dialogRef.close(this.importGuidelineData());
  }
}
